/**
 * useNativeAppUpdate (T27.2.9 D4) — the resting freshness word for the NATIVE
 * Mobile row ("is a newer build on my Play track?").
 *
 * The version-display Mobile row had no freshness segment on native (the web
 * `useFreshness` path can't apply — a bundled APK can't be "behind a deploy",
 * only behind a STORE update). This hook is the native counterpart: it runs the
 * same two-detector update check the launch/resume nudge uses
 * (`checkForAppUpdate()` in adapters/appUpdate.ts) once on mount and re-checks on
 * resume, then reports a small `{ updateAvailable, checked }` the surface feeds
 * into the EXISTING `freshnessLabel(...)` helper.
 *
 * Display-only. This hook drives the row WORD only (`✓ up to date` /
 * `⟳ Update available`). The ACTION — opening the Play Store — is the separate
 * launch/resume nudge (AppUpdatePrompt.tsx) that already ships; nothing here
 * offers a Refresh affordance (that's the web path).
 *
 * Platform contract. `checkForAppUpdate()` is itself a NO-OP off native Android
 * (returns not-available without importing the plugin or fetching), so on web /
 * iOS this hook never sets `checked` true and the surface omits the segment —
 * exactly as the row behaved before. Mirrors useFreshness's re-check on
 * `visibilitychange → visible`, but on the native `@capacitor/app`
 * `appStateChange → isActive` resume signal.
 *
 * Fail-soft. `checked` stays false until a check actually RESOLVES with a
 * definite answer, and is forced false when the check couldn't run (offline,
 * file 404, no Play track — `checkForAppUpdate` swallows those into
 * not-available + a diagnostic). The surface treats `!checked` as "latest
 * unknown" → freshnessLabel returns '' → the segment is omitted, never a false
 * `✓ up to date` or `⟳ Update available`.
 *
 * Stable mount (T27.2.9 fix). The effect must run ONCE per mount and never be
 * cancelled by an unrelated re-render. The surfaces that host this hook
 * (LoginVersionFooter, AppSettingsPage) carry three other async hooks
 * (useNativeAppVersion, useServerVersion, useFreshness) that each re-render as
 * they resolve. The default `isNative`/`check` are stable module-level
 * references, and the live options are read through a ref inside an
 * empty-dependency effect — so those sibling re-renders cannot re-run the effect,
 * trigger its cleanup, and suppress the in-flight check's `setState`. The check
 * completes once and sets `checked` regardless of unrelated re-renders.
 */

import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  checkForAppUpdate,
  type AppUpdateResult,
  type CheckForAppUpdateOptions,
} from '../adapters/appUpdate';

export interface NativeAppUpdate {
  /** True when a detector reported a newer build on the track. */
  updateAvailable: boolean;
  /**
   * False until a check resolves AND off native; false when the check couldn't
   * run. The surface omits the freshness word entirely while `checked` is false,
   * so it never raises a false freshness signal.
   */
  checked: boolean;
}

export interface UseNativeAppUpdateOptions {
  /**
   * Platform probe. Defaults to the real `Capacitor.isNativePlatform()`. Tests
   * inject `() => false` (web — inert) or `() => true` to drive the native path.
   */
  isNative?: () => boolean;
  /**
   * The update check. Defaults to the real `checkForAppUpdate`. Tests inject a
   * fake so the hook is pinned without a device or a real plugin/network. The
   * options it receives are forwarded from `checkOptions`.
   */
  check?: (options?: CheckForAppUpdateOptions) => Promise<AppUpdateResult>;
  /** Options forwarded to the check (diagnostics, manifest URL, etc.). */
  checkOptions?: CheckForAppUpdateOptions;
}

/**
 * Stable module-level default platform probe. Defined once (not a fresh arrow
 * per render) so a no-arg caller never feeds a new identity into the hook's
 * effect — see the "Stable mount" note above.
 */
const defaultIsNative = (): boolean => Capacitor.isNativePlatform();

/**
 * Read the native update freshness once on mount and again on each resume.
 *
 * Off native: inert — returns `{ updateAvailable: false, checked: false }` and
 * never runs the check, so the Mobile-row freshness word is omitted exactly as
 * before. On native: runs `checkForAppUpdate()` (its own no-op contract still
 * holds), sets `checked` true only when the check resolves, and re-runs on
 * `appStateChange → isActive`.
 */
export function useNativeAppUpdate(
  options: UseNativeAppUpdateOptions = {},
): NativeAppUpdate {
  const isNative = options.isNative ?? defaultIsNative;
  const check = options.check ?? checkForAppUpdate;
  const checkOptions = options.checkOptions;

  const [state, setState] = useState<NativeAppUpdate>({
    updateAvailable: false,
    checked: false,
  });

  // The live options are read through a ref so the mount effect can depend on
  // nothing and still see the latest impls. This is what makes an unrelated
  // re-render (a sibling async hook resolving) NOT re-run the effect — the prior
  // dep array `[isNative, check, checkOptions]` re-ran it every render when the
  // defaults were fresh arrows, cancelling the in-flight check before it set
  // `checked`. The platform branch is decided once, at mount, from this ref.
  const latest = useRef({ isNative, check, checkOptions });
  latest.current = { isNative, check, checkOptions };

  useEffect(() => {
    // Off native: never run the check; the surface omits the word (the pre-D4
    // behaviour). The check itself also no-ops, but short-circuiting here keeps
    // the hook from touching `@capacitor/app` on web. Decided ONCE at mount from
    // the ref — the platform doesn't change over a surface's lifetime.
    if (!latest.current.isNative()) return;

    let cancelled = false;

    const run = (): void => {
      const { check: liveCheck, checkOptions: liveOptions } = latest.current;
      void liveCheck(liveOptions).then(
        (result) => {
          if (cancelled) return;
          // A resolved check is a definite answer (fail-soft cases resolve to
          // not-available) → `checked` true so the word renders.
          setState({ updateAvailable: result.updateAvailable, checked: true });
        },
        () => {
          // Should not happen — checkForAppUpdate never rejects — but if a fake
          // or future impl throws, keep the word omitted (no false alarm).
          if (!cancelled) setState({ updateAvailable: false, checked: false });
        },
      );
    };

    run();

    // Re-check on resume — a build published while the app sat backgrounded is
    // picked up on return. Mirrors useFreshness's `visibilitychange → visible`,
    // on the native `appStateChange → isActive` signal. The plugin is reached
    // only on the native branch, via dynamic import (CP1) — never on web.
    let removeListener: (() => void) | undefined;
    void import('@capacitor/app')
      .then(({ App }) =>
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) run();
        }),
      )
      .then((handle) => {
        if (cancelled) {
          void handle.remove();
          return;
        }
        removeListener = () => void handle.remove();
      })
      .catch(() => {
        // No `@capacitor/app` (web / missing plugin) — the mount check already
        // ran; resume re-checks just won't fire. Fail-soft, no throw.
      });

    return () => {
      cancelled = true;
      removeListener?.();
    };
    // Empty deps: the effect runs ONCE per mount. Live `isNative`/`check`/
    // `checkOptions` are read from `latest.current`, so an unrelated re-render
    // never re-runs (and cancels) the in-flight check. Resume re-checks come
    // from the `appStateChange` listener, not a re-render. (The
    // react-hooks/exhaustive-deps rule is not registered in this project's
    // eslint config, so no disable directive is needed for the intentional [].)
  }, []);

  return state;
}
